import React, { useEffect, useRef, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';

interface Props {
  childProfiles: ChildProfile[];
  activeChildId: number | null;
  onSelect: (id: number | null) => void;
  /** Render the card non-interactive — no dropdown, no chevron. Used by the
   *  defaults modal while the sidebar child filter owns the child default. */
  readOnly?: boolean;
  /** Label shown on the card when no child is selected. */
  emptyLabel?: string;
  /** Label for the "no child" option in the dropdown. */
  noneLabel?: string;
}

/**
 * The hero child chip with its custom dropdown, shared by the session detail
 * panel and the new-session defaults modal. Relies on the flat `wsg-sdp__*`
 * classes in schedule.scss; sets `--child-color` on its own wrapper so it
 * works outside the `.wsg-sdp` panel root too.
 */
const ChildSelectCard: React.FC<Props> = ({
  childProfiles,
  activeChildId,
  onSelect,
  readOnly = false,
  emptyLabel = 'Choose a child',
  noneLabel = 'No child',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  // Clear the quick-find whenever the dropdown closes.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const activeChild = activeChildId != null
    ? childProfiles.find(c => c.id === activeChildId) ?? null
    : null;
  const childColor = activeChild?.colour_code ?? '#9aa89a';
  const childInitials = activeChild?.initials ?? '?';

  const handleSelect = (id: number | null) => {
    onSelect(id);
    setOpen(false);
  };

  const q = query.trim().toLowerCase();
  const filteredChildren = q
    ? childProfiles.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q))
    : childProfiles;

  return (
    <div
      className="wsg-sdp__child-wrap"
      data-help="schedule.child-select"
      ref={wrapRef}
      style={{ ['--child-color' as string]: childColor } as React.CSSProperties}
    >
      <button
        type="button"
        className={`wsg-sdp__child-card${open ? ' is-open' : ''}`}
        onClick={() => { if (!readOnly) setOpen(v => !v); }}
        style={readOnly ? { cursor: 'default' } : undefined}
        aria-haspopup={readOnly ? undefined : 'listbox'}
        aria-expanded={readOnly ? undefined : open}
        disabled={readOnly}
      >
        <span className="wsg-sdp__circle">{childInitials}</span>
        <div className="wsg-sdp__child-info">
          <span className="wsg-sdp__label">Child</span>
          <span className={`wsg-sdp__child-name${activeChild ? '' : ' wsg-sdp__child-name--empty'}`}>
            {activeChild
              ? `${activeChild.first_name} ${activeChild.last_name}`
              : emptyLabel}
          </span>
        </div>
        {!readOnly && (
          <i className={`fas fa-chevron-down wsg-sdp__chevron${open ? ' is-open' : ''}`} />
        )}
      </button>

      {open && !readOnly && (
        <div className="wsg-sdp__dropdown" role="listbox">
          <div className="wsg-sdp__search">
            <i className="fas fa-search" aria-hidden />
            <input
              type="text"
              placeholder="Search children…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              aria-label="Search children"
            />
          </div>

          {/* The "no child" option only shows when not filtering — a typed
              query means the user is hunting for a specific child. */}
          {!q && (
            <>
              <button
                type="button"
                role="option"
                aria-selected={activeChildId == null}
                className={`wsg-sdp__option${activeChildId == null ? ' is-selected' : ''}`}
                onClick={() => handleSelect(null)}
              >
                <span className="wsg-sdp__option-circle wsg-sdp__option-circle--empty">
                  <i className="fas fa-minus" />
                </span>
                <span className="wsg-sdp__option-name wsg-sdp__option-name--muted">
                  {noneLabel}
                </span>
                {activeChildId == null && (
                  <i className="fas fa-check wsg-sdp__option-check" />
                )}
              </button>

              {childProfiles.length > 0 && <div className="wsg-sdp__option-sep" />}
            </>
          )}

          {q && filteredChildren.length === 0 && (
            <div className="wsg-sdp__empty">No matches</div>
          )}

          {filteredChildren.map(c => {
            const selected = c.id === activeChildId;
            return (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={selected}
                className={`wsg-sdp__option${selected ? ' is-selected' : ''}`}
                onClick={() => handleSelect(c.id)}
                style={{ ['--option-color' as string]: c.colour_code || '#9aa89a' } as React.CSSProperties}
              >
                <span
                  className="wsg-sdp__option-circle"
                  style={{ background: c.colour_code || '#9aa89a' }}
                >
                  {c.initials}
                </span>
                <span className="wsg-sdp__option-name">
                  {c.first_name} {c.last_name}
                </span>
                {selected && (
                  <i className="fas fa-check wsg-sdp__option-check" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChildSelectCard;
